import Pusher from 'pusher-js';
import { useMemo, useContext, createContext } from 'react';
import * as PusherPushNotifications from '@pusher/push-notifications-web';

import { useSelector } from 'src/redux/store';
import { selectCurrentAuth } from 'src/redux/slices/auth';
// ----------------------------------------------------------------------

const initialState = new Pusher(`${import.meta.env.VITE_APP_KEY}`, {
  cluster: `${import.meta.env.VITE_APP_CLUSTER}`,
});

export const NotificationContext = createContext(initialState);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) throw new Error('useNotificationContext must be use inside SettingsProvider');

  return context;
};

// ----------------------------------------------------------------------

export const NotificationProvider = ({ children }: { children: any }) => {
  const { user, token } = useSelector(selectCurrentAuth);

  const pusher = useMemo(
    () =>
      new Pusher(`${import.meta.env.VITE_APP_KEY}`, {
        cluster: `${import.meta.env.VITE_APP_CLUSTER}`,
        authEndpoint: `${import.meta.env.VITE_APP_API_URL}/manage/notifications/auth`,
        auth: {
          params: {
            userId: user?.id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      }),
    [token, user]
  );

  try {
    if (import.meta.env.VITE_APP_PUSH_INSTANCE_ID) {
      window.navigator.serviceWorker.ready.then(() => {
        // Chrome
        const beamsClient = new PusherPushNotifications.Client({
          instanceId: `${import.meta.env.VITE_APP_PUSH_INSTANCE_ID}`,
        });

        const beamsTokenProvider = new PusherPushNotifications.TokenProvider({
          url: `${import.meta.env.VITE_APP_API_URL}/manage/notifications/pusher/beams-auth`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        beamsClient
          .start()
          .then(
            async () =>
              !(await beamsClient.getUserId().catch(() => false)) &&
              beamsClient.setUserId(user?.id ?? '', beamsTokenProvider)
          )
          .catch(console.error);
      });
    }
  } catch (error) {
    console.error(error);
  }

  return <NotificationContext.Provider value={pusher}>{children}</NotificationContext.Provider>;
};
